import { ed25519 } from "@noble/curves/ed25519.js";
import { KeypairType, StrKey, xdr } from "@stellar/stellar-sdk";
import { Buffer } from "buffer";

import { IKeybanApi } from "~/api";
import { Hex } from "~/index";

/**
 * StellarSigner
 * This class provides functionality for signing transactions and messages on the Stellar blockchain.
 * It closely resembles the Keypair interface from the stellar-sdk, but its signing methods are asynchronous.
 */
export class StellarSigner {
  type: KeypairType = "ed25519";

  #api: IKeybanApi;
  #clientShare: string;
  #publicKey: Hex;

  constructor(api: IKeybanApi, clientShare: string, publicKey: Hex) {
    this.#api = api;
    this.#clientShare = clientShare;
    this.#publicKey = publicKey;
  }

  publicKey(): string {
    return StrKey.encodeEd25519PublicKey(this.rawPublicKey());
  }

  rawPublicKey(): Buffer {
    const publicKey = this.#publicKey.replace(/^0x/, "");
    return Buffer.from(publicKey, "hex");
  }

  async sign(data: Buffer): Promise<Buffer> {
    const hexSignature = await this.#api.eddsa.sign(
      this.#clientShare,
      data.toString("hex"),
    );
    return Buffer.from(hexSignature, "hex");
  }

  async signDecorated(data: Buffer): Promise<xdr.DecoratedSignature> {
    const signature = await this.sign(data);
    const hint = this.signatureHint();

    return new xdr.DecoratedSignature({ hint, signature });
  }

  async signPayloadDecorated(data: Buffer): Promise<xdr.DecoratedSignature> {
    const signature = await this.sign(data);
    const keyHint = this.signatureHint();

    let hint = Buffer.from(data.slice(-4));
    if (hint.length < 4) {
      // append zeroes as needed
      hint = Buffer.concat([hint, Buffer.alloc(4 - data.length, 0)]);
    }

    return new xdr.DecoratedSignature({
      hint: hint.map((byte, i) => byte ^ keyHint[i]) as Buffer,
      signature,
    });
  }

  signatureHint(): Buffer {
    const a = this.xdrAccountId().toXDR();
    return a.slice(a.length - 4);
  }

  verify(data: Buffer, signature: Buffer): boolean {
    try {
      return ed25519.verify(signature, data, this.rawPublicKey());
    } catch {
      return false;
    }
  }

  xdrAccountId(): xdr.AccountId {
    return xdr.PublicKey.publicKeyTypeEd25519(this.rawPublicKey());
  }

  xdrPublicKey(): xdr.PublicKey {
    return xdr.PublicKey.publicKeyTypeEd25519(this.rawPublicKey());
  }

  xdrMuxedAccount(id: string): xdr.MuxedAccount {
    if (typeof id !== "undefined") {
      if (typeof id !== "string") {
        throw new TypeError(`expected string for ID, got ${typeof id}`);
      }

      return xdr.MuxedAccount.keyTypeMuxedEd25519(
        new xdr.MuxedAccountMed25519({
          id: xdr.Uint64.fromString(id),
          ed25519: this.rawPublicKey(),
        }),
      );
    }

    return xdr.MuxedAccount.keyTypeEd25519(this.rawPublicKey());
  }
}
